'use client';

import { useState } from 'react';

import Link from 'next/link';

import type { Provider } from '@supabase/supabase-js';

import { SignUpMethodsContainer } from '@kit/auth/sign-up';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import pathsConfig from '~/config/paths.config';

/**
 * Owns the sign-up page's own copy so it can react to the flow finishing.
 *
 * The heading, the subheading and the "already have an account" link all
 * describe a form that is no longer on screen once the confirmation email has
 * been sent, so they step aside for the verify panel's own wording.
 */
export function SignUpView(props: {
  providers: {
    password: boolean;
    magicLink: boolean;
    otp: boolean;
    oAuth: Provider[];
  };

  displayTermsCheckbox?: boolean;
  captchaSiteKey?: string;

  /** Legacy `?plan=` links can still reach this page. */
  plan?: string;
  inviteToken?: string;

  paths: {
    callback: string;
    appHome: string;
  };
}) {
  const [verifying, setVerifying] = useState(false);

  return (
    <>
      <If condition={!verifying}>
        <div className={'flex flex-col gap-1'}>
          <p className="text-primary flex items-center gap-1.5 text-xs font-bold tracking-wide uppercase">
            <span className="bg-primary inline-block size-1.5 rounded-full" />
            <Trans i18nKey={'auth:signUpEyebrow'} />
          </p>

          <h1 className="text-foreground scroll-m-20 text-3xl font-bold tracking-tight lg:text-4xl">
            <Trans i18nKey={'auth:signUpHeading'} />
          </h1>

          {/* Legacy pricing links lead to the same single software trial. */}
          {props.plan === 'pro' || props.plan === 'free' ? (
            <p className="border-border bg-muted text-muted-foreground mt-3 rounded-lg border px-3 py-2 text-sm">
              Your 14-day SupplyAutomate trial starts here. No credit card
              required. Then $199/month or $1,999/year.
            </p>
          ) : null}
        </div>
      </If>

      <SignUpMethodsContainer
        providers={props.providers}
        displayTermsCheckbox={props.displayTermsCheckbox}
        paths={props.paths}
        captchaSiteKey={props.captchaSiteKey}
        onVerificationChange={setVerifying}
      />

      <If condition={!verifying}>
        <div className={'text-muted-foreground flex justify-start text-sm'}>
          <span>
            <Trans i18nKey={'auth:alreadyHaveAnAccount'} />
          </span>

          <Button
            asChild
            variant={'link'}
            size={'sm'}
            className={'h-auto py-0'}
          >
            <Link
              href={
                props.inviteToken
                  ? `${pathsConfig.auth.signIn}?invite_token=${encodeURIComponent(props.inviteToken)}`
                  : pathsConfig.auth.signIn
              }
              prefetch={true}
            >
              <Trans i18nKey={'auth:signInLinkAction'} />
            </Link>
          </Button>
        </div>
      </If>
    </>
  );
}
